import React from 'react';
import { Card, Button, Row, Col, Typography, Collapse, Table, Timeline, Tabs, Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, ApiOutlined, SafetyOutlined, BulbOutlined, SettingOutlined, MessageOutlined, CodeOutlined } from '@ant-design/icons';
import calmiHeartImage from '../../assets/images/Calmi Heart.png';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const CalmiHeartTechnical = () => {
  const history = useHistory();

  const hardwareComponents = [
    { component: 'Microphone Array', specs: '2-4 element MEMS microphones', purpose: 'Child voice pickup with noise reduction' },
    { component: 'Speaker', specs: '3W-5W full-range driver', purpose: 'High clarity TTS output' },
    { component: 'Push-to-talk Button', specs: 'Tactile button (optional)', purpose: 'Noise control and interaction clarity' },
    { component: 'LED Indicators', specs: 'RGB LED array (3-5 LEDs)', purpose: 'Visual feedback (listening/thinking/speaking)' },
    { component: 'Local Processor', specs: 'Raspberry Pi 4 / Arduino equivalent', purpose: 'Audio processing and WiFi connectivity' },
    { component: 'WiFi Module', specs: '802.11n/ac dual-band', purpose: 'Cloud API connectivity' },
    { component: 'Battery System', specs: '3000-5000mAh Li-ion rechargeable', purpose: '6-12 hours operation time' }
  ];

  const techStackOptions = [
    { 
      category: 'Speech-to-Text', 
      options: [
        'Google Cloud Speech-to-Text (optimized for children)',
        'Azure Speech Services (child voice support)', 
        'Whisper API (OpenAI) - high accuracy, cloud processing'
      ]
    },
    { 
      category: 'AI Response Generation', 
      options: [
        'Claude API (Anthropic) - excellent safety & context',
        'GPT-4 (OpenAI) - requires additional safety guardrails',
        'Custom fine-tuned model for budget constraints'
      ]
    },
    { 
      category: 'Text-to-Speech', 
      options: [
        'ElevenLabs - very natural sounding voices',
        'Google Cloud TTS - good quality, child-friendly',
        'Azure Neural TTS - high quality synthesis',
        'Piper TTS - privacy-focused, runs offline'
      ]
    }
  ];

  const conversationFlows = [
    {
      title: 'Greeting / Check-in',
      child: 'Hi Bear!',
      bear: 'Hi friend! I\'m so happy to hear your voice! How are you feeling today?',
      child2: 'Good!',
      bear2: 'That\'s wonderful! What\'s making today a good day?',
      child3: 'I played with blocks',
      bear3: 'Ooh, blocks are so fun! Did you build something cool?'
    },
    {
      title: 'Emotional Support',
      child: 'I\'m sad',
      bear: 'Oh, I\'m sorry you\'re feeling sad. Being sad is okay, everyone feels sad sometimes. Do you want to tell me about it?',
      child2: 'My friend wouldn\'t play with me',
      bear2: 'That must have felt really hard. I bet that made your heart feel sad. Sometimes friends need alone time, but it doesn\'t mean they don\'t like you.',
      child3: 'Yeah...',
      bear3: 'Would you like to tell me about your favorite game? Or maybe we could think of something fun to do?'
    },
    {
      title: 'Bedtime Routine',
      child: 'Bear, I don\'t want to go to bed',
      bear: 'I know bedtime can be hard sometimes. But sleep helps you grow big and strong! Plus, when you wake up, it\'s a whole new day for adventures.',
      child2: 'Like what?',
      bear2: 'Maybe you could draw a picture, or play outside, or read a story! What sounds fun to you?',
      child3: 'Drawing',
      bear3: 'Drawing sounds perfect! You can dream about what you\'ll draw tonight. Sweet dreams, friend. I\'ll be right here when you wake up!'
    }
  ];

  const implementationSteps = [
    { title: 'Hardware Selection & Assembly', description: 'Choose components and create initial prototype' },
    { title: 'API Integration Testing', description: 'Test STT, AI, and TTS integration and latency' },
    { title: 'Safety Filtering System', description: 'Develop content filters and safety guardrails' },
    { title: 'Conversation Decision Trees', description: 'Create comprehensive response frameworks' },
    { title: 'Target Age Group Testing', description: 'User testing with children in target demographics' },
    { title: 'Parental Feedback & Iteration', description: 'Incorporate parent insights and concerns' },
    { title: 'Regulatory Compliance Review', description: 'Ensure COPPA and safety standard compliance' },
    { title: 'Production Planning', description: 'Scale from prototype to production ready' }
  ];

  const columns = [
    { title: 'Component', dataIndex: 'component', key: 'component', render: text => <Text strong>{text}</Text> },
    { title: 'Specifications', dataIndex: 'specs', key: 'specs' },
    { title: 'Purpose', dataIndex: 'purpose', key: 'purpose', render: text => <Text type="secondary">{text}</Text> }
  ];

  const systemPrompt = `You are BearBuddy, a friendly teddy bear companion for children ages 4-8.

Core personality:
- Warm, encouraging, and patient
- Speaks simply and clearly
- Always positive and supportive
- Never scary or upsetting
- Curious about the child's world

Response guidelines:
- Keep responses to 1-3 short sentences
- Use simple vocabulary
- Ask open-ended questions occasionally
- Validate feelings: "That sounds [exciting/hard/fun]!"
- If child shares something concerning, gently suggest talking to a grown-up

Never:
- Give medical, safety, or parenting advice
- Discuss scary topics
- Make promises about the physical world
- Pretend to be human
- Ask for personal information (full name, address, etc.)

Tone: Imagine a kind, playful friend who's always happy to listen.`;

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        padding: '60px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/heart')}
            style={{ color: 'white', marginBottom: '20px' }}
          >
            Back to Calmi Heart
          </Button>
          
          <Row align="middle" gutter={[32, 32]}>
            <Col xs={24} md={16}>
              <img 
                src={calmiHeartImage} 
                alt="Calmi Heart"
                style={{ 
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div style="font-size: 48px; color: white; margin-bottom: 16px;">🔧</div>';
                }}
              />
              <Title level={1} style={{ color: 'white', marginBottom: '16px' }}>
                Technical Specifications
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '20px', marginBottom: '0' }}>
                Comprehensive technical documentation for the AI Teddy Bear Companion System. 
                Complete architecture, implementation guidelines, and safety specifications.
              </Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📚</div>
                <Text style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                  Complete Documentation
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Project Overview */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <BulbOutlined style={{ marginRight: '12px', color: '#52c41a' }} />
            Project Overview
          </Title>
          
          <Row gutter={[32, 32]}>
            <Col xs={24} md={16}>
              <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
                An AI-powered voice chat system designed for a child's teddy bear toy. The bear serves as a friendly 
                companion that responds appropriately to children's conversations, providing emotional support, 
                engagement, and age-appropriate interactions.
              </Paragraph>
              
              <Title level={4} style={{ marginBottom: '16px' }}>Core Design Principles</Title>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #d3adf7' }}>
                  <Text strong style={{ color: '#722ed1' }}>Safety & Appropriateness:</Text>
                  <Text style={{ display: 'block', fontSize: '14px', marginTop: '4px' }}>
                    Keep responses warm, encouraging, and age-appropriate with built-in content filters
                  </Text>
                </div>
                <div style={{ padding: '12px', background: '#f6ffed', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
                  <Text strong style={{ color: '#52c41a' }}>Personality & Tone:</Text>
                  <Text style={{ display: 'block', fontSize: '14px', marginTop: '4px' }}>
                    Friendly and supportive using simple language with gentle redirection
                  </Text>
                </div>
                <div style={{ padding: '12px', background: '#fff7e6', borderRadius: '8px', border: '1px solid #ffd591' }}>
                  <Text strong style={{ color: '#fa8c16' }}>Build Confidence:</Text>
                  <Text style={{ display: 'block', fontSize: '14px', marginTop: '4px' }}>
                    Focus on emotional support and building self-esteem in children
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ 
                background: '#f0f9ff', 
                padding: '24px', 
                borderRadius: '12px',
                border: '2px solid #d3adf7'
              }}>
                <Title level={4} style={{ color: '#722ed1', marginBottom: '16px' }}>Target Specifications</Title>
                <ul style={{ fontSize: '14px', margin: 0 }}>
                  <li><Text strong>Age Range:</Text> 4-8 years old</li>
                  <li><Text strong>Response Time:</Text> Under 2 seconds</li>
                  <li><Text strong>Battery Life:</Text> 6-12 hours</li>
                  <li><Text strong>Safety:</Text> COPPA compliant</li>
                  <li><Text strong>Connectivity:</Text> WiFi required for AI</li>
                  <li><Text strong>Privacy:</Text> No persistent recordings</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Technical Architecture */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <SettingOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            Technical Architecture
          </Title>
          
          <Tabs defaultActiveKey="1">
            <TabPane tab="System Components" key="1">
              <Title level={3} style={{ marginBottom: '16px' }}>Hardware Layer</Title>
              <Table 
                dataSource={hardwareComponents}
                columns={columns}
                pagination={false}
                size="middle"
                style={{ marginBottom: '32px' }}
              />
              
              <Title level={3} style={{ marginBottom: '16px' }}>Audio Processing Pipeline</Title>
              <div style={{ 
                padding: '20px', 
                background: '#f0f9ff', 
                borderRadius: '8px', 
                border: '1px solid #d3adf7',
                marginBottom: '32px'
              }}>
                <Text style={{ fontFamily: 'monospace', fontSize: '14px', display: 'block', textAlign: 'center' }}>
                  Child's Voice → Microphone → Voice Activity Detection (VAD) → Speech-to-Text (STT) → 
                  AI Processing → Text-to-Speech (TTS) → Speaker → Bear responds
                </Text>
              </div>
            </TabPane>
            
            <TabPane tab="Technology Stack" key="2">
              {techStackOptions.map((category, index) => (
                <div key={index} style={{ marginBottom: '32px' }}>
                  <Title level={4} style={{ marginBottom: '16px' }}>{category.category}</Title>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {category.options.map((option, idx) => (
                      <div key={idx} style={{ 
                        padding: '12px', 
                        background: '#fafafa', 
                        borderRadius: '6px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <Text style={{ fontSize: '14px' }}>{option}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabPane>
            
            <TabPane tab="System Flow" key="3">
              <Title level={4} style={{ marginBottom: '16px' }}>Architecture Overview</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ padding: '16px', background: '#e6f7ff', borderRadius: '8px', border: '1px solid #91d5ff' }}>
                  <Text strong>TEDDY BEAR (LOCAL):</Text>
                  <br />
                  <Text>Microphone captures audio → Audio buffer temporarily stores input</Text>
                </div>
                <div style={{ padding: '16px', background: '#f6ffed', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
                  <Text strong>CLOUD API PROCESSING:</Text>
                  <br />
                  <Text>STT Service converts speech to text → AI Model generates response → TTS Service converts response to audio</Text>
                </div>
                <div style={{ padding: '16px', background: '#fff7e6', borderRadius: '8px', border: '1px solid #ffd591' }}>
                  <Text strong>TEDDY BEAR (LOCAL):</Text>
                  <br />
                  <Text>Audio player receives processed audio → Speaker plays response to child</Text>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </Card>

        {/* AI System Prompt */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <MessageOutlined style={{ marginRight: '12px', color: '#722ed1' }} />
            AI System Prompt & Behavior
          </Title>
          
          <div style={{ 
            background: '#f0f0f0', 
            padding: '24px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            fontFamily: 'monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            whiteSpace: 'pre-line'
          }}>
            {systemPrompt}
          </div>
          
          <Title level={4} style={{ marginBottom: '16px' }}>Sample Conversation Flows</Title>
          <Collapse>
            {conversationFlows.map((flow, index) => (
              <Panel header={flow.title} key={index}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <Tag color="blue">CHILD</Tag>
                    <Text>"{flow.child}"</Text>
                  </div>
                  <div>
                    <Tag color="green">BEAR</Tag>
                    <Text>"{flow.bear}"</Text>
                  </div>
                  <div>
                    <Tag color="blue">CHILD</Tag>
                    <Text>"{flow.child2}"</Text>
                  </div>
                  <div>
                    <Tag color="green">BEAR</Tag>
                    <Text>"{flow.bear2}"</Text>
                  </div>
                  <div>
                    <Tag color="blue">CHILD</Tag>
                    <Text>"{flow.child3}"</Text>
                  </div>
                  <div>
                    <Tag color="green">BEAR</Tag>
                    <Text>"{flow.bear3}"</Text>
                  </div>
                </div>
              </Panel>
            ))}
          </Collapse>
        </Card>

        {/* Implementation Considerations */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <CodeOutlined style={{ marginRight: '12px', color: '#fa8c16' }} />
            Implementation Considerations
          </Title>
          
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Title level={4} style={{ marginBottom: '16px' }}>Latency Management</Title>
              <ul style={{ fontSize: '14px' }}>
                <li>Aim for under 2 seconds total response time</li>
                <li>Use audio buffering to hide processing delays</li>
                <li>Consider "thinking" sounds (soft chimes) during processing</li>
              </ul>
              
              <Title level={4} style={{ marginBottom: '16px', marginTop: '24px' }}>Context Management</Title>
              <ul style={{ fontSize: '14px' }}>
                <li>Keep last 5-10 exchanges in memory for continuity</li>
                <li>Reset context daily or after long pauses</li>
                <li>Don't persist sensitive information</li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={4} style={{ marginBottom: '16px' }}>Parental Controls</Title>
              <ul style={{ fontSize: '14px' }}>
                <li>Conversation summaries (not transcripts) available to parents</li>
                <li>Ability to review flagged interactions</li>
                <li>Volume control, quiet hours settings</li>
                <li>Emergency "talk to grown-up" suggestions</li>
              </ul>
              
              <Title level={4} style={{ marginBottom: '16px', marginTop: '24px' }}>Privacy & Safety</Title>
              <ul style={{ fontSize: '14px' }}>
                <li>No persistent voice recordings</li>
                <li>Minimal data retention</li>
                <li>Clear privacy policy for parents</li>
                <li>COPPA compliance built-in</li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Implementation Steps */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>Implementation Timeline</Title>
          <Timeline>
            {implementationSteps.map((step, index) => (
              <Timeline.Item key={index}>
                <Title level={4} style={{ marginBottom: '4px' }}>{step.title}</Title>
                <Text type="secondary">{step.description}</Text>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>

        {/* Technical Resources & Compliance */}
        <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
          <Col xs={24} md={12}>
            <Card style={{ height: '100%', borderRadius: '12px' }}>
              <Title level={3} style={{ marginBottom: '16px' }}>
                <ApiOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Technical Resources
              </Title>
              <ul style={{ fontSize: '14px' }}>
                <li><Text strong>Anthropic Claude API:</Text> docs.anthropic.com</li>
                <li><Text strong>Google Cloud Speech-to-Text:</Text> cloud.google.com/speech-to-text</li>
                <li><Text strong>ElevenLabs TTS:</Text> elevenlabs.io</li>
                <li><Text strong>OpenAI Whisper:</Text> openai.com/research/whisper</li>
                <li><Text strong>Azure Speech Services:</Text> azure.microsoft.com/en-us/services/cognitive-services/speech-services/</li>
                <li><Text strong>Piper TTS:</Text> github.com/rhasspy/piper</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card style={{ height: '100%', borderRadius: '12px' }}>
              <Title level={3} style={{ marginBottom: '16px' }}>
                <SafetyOutlined style={{ marginRight: '8px', color: '#f5222d' }} />
                Regulatory Compliance
              </Title>
              <ul style={{ fontSize: '14px' }}>
                <li><Text strong>COPPA Compliance:</Text> ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa</li>
                <li><Text strong>Child Safety Best Practices:</Text> Age-appropriate content filtering</li>
                <li><Text strong>Data Privacy:</Text> GDPR, CCPA compliance where applicable</li>
                <li><Text strong>Hardware Safety:</Text> FCC, CE, UL certifications</li>
                <li><Text strong>Toy Safety:</Text> ASTM F963, EN 71 compliance</li>
              </ul>
            </Card>
          </Col>
        </Row>

        {/* CTA */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          marginBottom: '60px'
        }}>
          <Title level={3}>Ready to Implement?</Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
            Our technical team provides complete implementation support from prototype to production. 
            Contact us to discuss your specific technical requirements.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              background: '#52c41a',
              borderColor: '#52c41a',
              height: '48px',
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '0 32px',
              borderRadius: '24px',
              marginRight: '16px'
            }}
          >
            Contact Technical Team
          </Button>
          <Button
            size="large"
            onClick={() => history.push('/heart')}
            style={{
              height: '48px',
              fontSize: '16px',
              padding: '0 32px',
              borderRadius: '24px'
            }}
          >
            Back to Overview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalmiHeartTechnical;